import React, { useState } from 'react';
import type { PatientProfileTab, PatientRecord } from '../../patients/types';
import { PT_CONTACT_FLAGS } from '../../patients-list/contactFlags';
import { PatientProfileSectionCard as ContactSectionCard } from './PatientProfileSectionCard';
import { Avatar } from '../Avatar/Avatar';
import { PatientClinicalAlerts } from './PatientClinicalAlerts';
import { PatientProfileHero } from './PatientProfileHero';
import { PatientProfileSectionCard } from './PatientProfileSectionCard';
import { PatientVisitHistory } from './PatientVisitHistory';
import { PatientActionBand } from './profile-v2/PatientActionBand';
import { PatientComfortCard } from './profile-v2/PatientComfortCard';
import { PatientFinancialCardV2 } from './profile-v2/PatientFinancialCardV2';
import { PatientNextVisitCard } from './profile-v2/PatientNextVisitCard';
import { PatientMessageModal } from './profile-v2/PatientMessageModal';
import { PatientStaffNotesCard } from './profile-v2/PatientStaffNotesCard';

export interface PatientProfileTabContentV2Props {
  patient: PatientRecord;
  onOpenTab?: (tab: PatientProfileTab) => void;
  /** Navigate to a relative who is also a patient. */
  onSelectPatient?: (patientId: string) => void;
  onSendMessage?: (text: string) => void;
  /** Opens AI Brain › General card 05, where a language is switched on (§5). */
  onOpenLanguages?: (() => void) | undefined;
}

function BulletList({ items, dotColor }: { items: string[]; dotColor: string }) {
  return (
    <div className="flex flex-col gap-[9px]">
      {items.map((s) => (
        <div key={s} className="flex gap-[9px] text-[13px] leading-[1.45] text-[var(--ink-1)]">
          <span
            className="mt-[7px] h-[5px] w-[5px] shrink-0 rounded-full"
            style={{ background: dotColor }}
          />
          {s}
        </div>
      ))}
    </div>
  );
}

/**
 * Patient profile, v2 — front-desk-first.
 *
 * Consent lives in the hero as a single "ELVA essentials" chip rather than its
 * own row: one grant is one fact, and it belongs with the patient's identity.
 * Financial sits above Clinical alerts because the desk is asked about money
 * far more often than about medications. Family rows link to relatives who are
 * also patients, and Message opens a send dialog that refuses to send to an
 * opted-out number.
 *
 * The page is read with the patient on the phone or at the desk, so the order
 * answers their questions in the order they come up: who is this → what do I
 * need to sort out while I have them → how do I talk to them → what does the
 * record say.
 *
 * Two structural changes from v1: the action band (the profile could not
 * previously say what was outstanding, though the directory card could), and
 * notes (what a colleague did, as opposed to what we observed about the
 * patient). Intelligence blocks stay plain prose, with a date so a note from
 * three years ago stops reading as current.
 */
export function PatientProfileTabContentV2({
  patient: p,
  onOpenTab,
  onSelectPatient,
  onSendMessage,
  onOpenLanguages,
}: PatientProfileTabContentV2Props) {
  const dates = p.intelligenceDates ?? {};
  const [messageOpen, setMessageOpen] = useState(false);

  return (
    <>
      <PatientProfileHero
        patient={p}
        onMessage={() => {
          setMessageOpen(true);
        }}
        onOpenLanguages={onOpenLanguages}
      />

      <PatientActionBand
        todos={p.todos ?? []}
        {...(onOpenTab ? { onOpenTab } : {})}
      />

      <div
        className="pp-fade grid items-start gap-4"
        style={{
          gridTemplateColumns: 'minmax(0,1.5fr) minmax(0,1fr)',
          animationDelay: '120ms',
        }}
      >
        <div className="flex min-w-0 flex-col gap-4">
          <PatientProfileSectionCard
            icon="doc"
            title="Doctor's note"
            accent="var(--ai-4)"
            {...(dates.doctorNote ? { subtitle: `Updated ${dates.doctorNote}` } : {})}
          >
            <p className="m-0 text-[13.5px] leading-[1.6] text-[var(--ink-1)]">{p.doctorNote}</p>
          </PatientProfileSectionCard>

          <PatientProfileSectionCard
            icon="clock"
            title="Last interaction"
            accent="var(--gold)"
            {...(dates.lastInteraction ? { subtitle: dates.lastInteraction } : {})}
          >
            <p className="m-0 text-[13.5px] leading-[1.6] text-[var(--ink-1)]">
              {p.lastInteraction}
            </p>
          </PatientProfileSectionCard>

          {/* Cultural notes removed: read_cultural_notes guards on a
              `patient_intelligence` app that does not exist, so the block could
              only ever render an empty list. */}
          <PatientProfileSectionCard
            icon="sparkle"
            title="Conversation starters"
            accent="var(--ai-3)"
            {...(dates.starters ? { subtitle: `Updated ${dates.starters}` } : {})}
          >
            <BulletList items={p.starters} dotColor="var(--ai-3)" />
          </PatientProfileSectionCard>

          <PatientComfortCard patient={p} updatedAt={dates.comfort} />

          <PatientStaffNotesCard notes={p.staffNotes ?? []} />

          <PatientProfileSectionCard
            icon="team"
            title={`Family · ${String(p.family.length)}`}
            accent="var(--ai-3)"
          >
            {p.family.length === 0 ? (
              <p className="m-0 text-[13px] text-[var(--ink-3)]">No family on file</p>
            ) : (
              <div className="flex flex-col gap-[11px]">
                {p.family.map((f) => {
                  const linkId = f.patientId;
                  const Row = (
                    <>
                      <Avatar name={f.name} hue={f.overdue ? 'var(--gold)' : '#5D8F8F'} size={26} />
                      <span
                        className="font-semibold"
                        style={{ color: linkId ? 'var(--ai-3)' : 'var(--ink-0)' }}
                      >
                        {f.name}
                      </span>
                      <span className="text-[var(--ink-4)]">·</span>
                      <span className="text-[var(--ink-3)]">{f.rel}</span>
                      <span
                        className="ml-auto text-xs font-semibold"
                        style={{ color: f.overdue ? 'var(--gold)' : 'var(--success)' }}
                      >
                        {f.status}
                      </span>
                    </>
                  );
                  return linkId ? (
                    <button
                      key={f.name}
                      type="button"
                      className="flex w-full items-center gap-2.5 rounded-[var(--r-md)] border-0 bg-transparent p-0 text-left text-[13px] hover:bg-[var(--cream-0)]"
                      onClick={() => {
                        onSelectPatient?.(linkId);
                      }}
                    >
                      {Row}
                    </button>
                  ) : (
                    <div key={f.name} className="flex items-center gap-2.5 text-[13px]">
                      {Row}
                    </div>
                  );
                })}
              </div>
            )}
          </PatientProfileSectionCard>
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          <PatientNextVisitCard patient={p} />
          <PatientFinancialCardV2 patient={p} />
          <PatientContactCard patient={p} />
          <PatientClinicalAlerts clinical={p.clinical} />
        </div>
      </div>

      <PatientVisitHistory patient={p} showCounter={false} />

      {/* The visit history table used to sit flush against the bottom of the
          scroll container with nothing after it, which reads as a cut-off page. */}
      <div className="h-16 shrink-0" aria-hidden />

      <PatientMessageModal
        patient={p}
        open={messageOpen}
        onClose={() => {
          setMessageOpen(false);
        }}
        {...(onSendMessage ? { onSend: onSendMessage } : {})}
      />
    </>
  );
}

/**
 * Patient contact card (amendment 32 §5) — Texts / Email / Calls each on or off
 * with since and how, the topics a patient has stopped, and — when texts are off
 * — the line that says who can turn them back on (only the patient can text START).
 */
function PatientContactCard({ patient }: { patient: PatientRecord }) {
  const flag = PT_CONTACT_FLAGS.find((f) => f.id === patient.id || f.name === patient.name);
  const textsOff = flag?.textsOff;
  const stopped = flag?.stoppedTopic;
  const first = patient.first !== '' ? patient.first : (patient.name.split(' ')[0] ?? 'they');

  const Channel = ({ label, on, detail }: { label: string; on: boolean; detail?: string | undefined }) => (
    <div className="flex items-baseline justify-between gap-3 border-b border-[var(--cream-2)] py-2 last:border-0">
      <span className="text-[13px] text-[var(--ink-1)]">{label}</span>
      <span className="text-right">
        <span className={on ? 'text-[13px] font-semibold text-[var(--success)]' : 'text-[13px] font-semibold text-[var(--terracotta)]'}>{on ? 'On' : 'Off'}</span>
        {detail !== undefined ? <span className="block text-[11px] text-[var(--ink-4)]">{detail}</span> : null}
      </span>
    </div>
  );

  return (
    <ContactSectionCard icon="phone" title="Contact" accent="var(--ai-3)">
      <Channel label="Texts" on={textsOff === undefined} detail={textsOff !== undefined ? `since ${textsOff.since} · ${textsOff.how}` : undefined} />
      <Channel label="Email" on />
      <Channel label="Calls" on />
      <div className="mt-2 flex items-baseline justify-between gap-3">
        <span className="text-[13px] text-[var(--ink-1)]">Topics</span>
        <span className="text-right text-[12.5px] text-[var(--ink-3)]">{stopped !== undefined ? `No ${stopped.topic} · since ${stopped.since}` : 'None stopped'}</span>
      </div>
      {textsOff !== undefined ? (
        <div className="mt-3 rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--ai-3)_25%,transparent)] bg-[var(--ai-tint)] px-3 py-2 text-[12px] leading-[1.5] text-[var(--ai-3)]">
          To turn texts back on, ask {first} to text <strong>START</strong> to (210) 555-0140. Only {first === 'they' ? 'they' : first} can.
        </div>
      ) : null}
    </ContactSectionCard>
  );
}
